import { AxiosResponse } from "axios";
import { useEffect, useRef, useState } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import { GetColonyLookup, GetOgranismLookup, GetResultSummary, GetSensitiResultData, PublishAndValidate, SaveResultSummary, UpdateLisStatus } from "Services/MicrobiologyResultData";
import { Loader } from "Shared/Common/Loader";
import useLang from "Shared/hooks/useLanguage";
import {
  reactSelectSMStyle2,
  styles
} from "../../../Utils/Common";
import PermissionComponent, {
  getPermissionDisplayName,
} from "../../../Shared/Common/Permissions/PermissionComponent";
import RejectModal from "./Modal/RejectModal";
import ViewHistoricalDataModal from "./Modal/ViewHistoricalDataModal";
import { useMicrobiologyResultDataContext } from "./ResultDataContext";
import SensitivityDataTable from "./SensitivityDataTable";
export interface ReferenceLab {
  value: number;
  label: string;
}

interface ExpandableI {
  requisitionOrderId: number;
  requisitionId?: number;
}

export interface OptionType {
  value: number;
  label: string;
}

const PANEL_BUTTON_COLORS = ["#28a745", "#007bff", "#6f42c1", "#fd7e14", "#20c997", "#e83e8c", "#17a2b8", "#6c757d"];

const ResultDataExpandableRow = (props: ExpandableI) => {
  const { t } = useLang();
  const { requisitionOrderId, requisitionId } = props;
  const {
    filterData,
    loadGridData,
    data,
    rowsToExpand,
    setRowsToExpand,
    isMasterExpandTriggered,
  } = useMicrobiologyResultDataContext();

  const targetRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdatingLisStatus, setIsUpdatingLisStatus] = useState(false);
  const [updatingLisStatusAction, setUpdatingLisStatusAction] = useState<"publish" | "complete" | null>(null);
  const [isNoSensitizerSaving, setIsNoSensitizerSaving] = useState(false);
  const [organismOptions, setOrganismOptions] = useState<any[]>([]);
  const [colonyCountOptions, setColonyCountOptions] = useState<any[]>([]);
  const [panelsFromSummary, setPanelsFromSummary] = useState<any[]>([]);
  const [summaries, setSummaries] = useState<any[]>([]);
  const [showViewHistoricalData, setShowViewHistoricalData] = useState(false);
  const [sensitivityData, setSensitivityData] = useState<any[]>([]);
  const [showRejectModal, setShowRejectModal] = useState(false);

  const [plateData, setPlateData] = useState<any>({
    selectedOrganism: "",
    colonyCount: "",
    bap: "",
    mac: "",
    internalComments: "",
    significantFindings: "",
    plateDescriptions: [],
  });

  const saveMicrobiologyExpandResultData = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    // Build payload for SaveResultSummary using all summaries
    const resultSummaryPayload = (summaries && summaries.length > 0
      ? summaries
      : [
        {
          id: 0,
          requsitionOrderId: requisitionOrderId,
          organismId: plateData.selectedOrganism || 0,
          colonyId: plateData.colonyCount || 0,
          internalComments: plateData.internalComments || "",
          significantFinding: plateData.significantFindings || "",
          tabId: filterData?.tabId || 0,
          plateDescriptions: plateData.plateDescriptions || [],
          panels: panelsFromSummary || [],
        },
      ]).map((s: any) => ({
        id: s.id || 0,
        requsitionOrderId: s.requsitionOrderId || s.requisitionOrderId || requisitionOrderId,
        organismId: s.organismId || s.organismId === 0 ? s.organismId : s.organismId || 0,
        colonyId: s.colonyId || s.colonyId === 0 ? s.colonyId : s.colonyId || 0,
        internalComments: s.internalComments || s.internalComment || "",
        significantFinding: s.significantFinding || s.significantFinding || "",
        tabId: s.tabId || filterData?.tabId || 0,
        plateDescriptions: Array.isArray(s.plateDescriptions) ? s.plateDescriptions : [],
        panels: Array.isArray(s.panels) ? s.panels : [],
      }));

    const summaryPayload = { resultSummary: resultSummaryPayload };

    try {
      const saveRes: AxiosResponse = await SaveResultSummary(summaryPayload);
      if (saveRes?.data?.statusCode === 200) {
        toast.success(t(saveRes?.data?.message || "Result summary saved"));
      } else {
        toast.error(t(saveRes?.data?.message || "Failed to save result summary"));
      }
    } catch (err: any) {
      console.trace("Error saving result summary:", err);
      toast.error(t("Error saving result summary"));
    } finally {
      setIsSubmitting(false);
      loadGridData(false);
    }
  }

  useEffect(() => {
    const init = async () => {
      setLoading(true);

      try {
        const orgRes: any = await GetOgranismLookup();
        if (orgRes?.data?.result && Array.isArray(orgRes.data.result)) {
          setOrganismOptions(orgRes.data.result);
        }
      } catch (err: any) {
        console.error("Error fetching organism lookup:", err);
      }

      try {
        const colRes: any = await GetColonyLookup();
        if (colRes?.data?.result && Array.isArray(colRes.data.result)) {
          setColonyCountOptions(colRes.data.result);
        }
      } catch (err: any) {
        console.error("Error fetching colony lookup:", err);
      }

      try {
        const summaryRes: AxiosResponse = await GetResultSummary({
          requisitionOrderId: requisitionOrderId,
          tabId: filterData?.tabId,
        });
        const raw = summaryRes?.data;

        const summaryArray =
          Array.isArray(raw?.resultSummary) ? raw.resultSummary
            : Array.isArray(raw?.data?.resultSummary) ? raw.data.resultSummary
              : Array.isArray(raw?.data) ? raw.data
                : [];
        if (summaryArray.length > 0) {
          setSummaries(summaryArray);
        } else {
          setPanelsFromSummary([]);
          setSummaries([]);
        }
      } catch (err: any) {
        console.error("Error fetching result summary:", err);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  const updateSummaryField = (index: number, field: string, value: any) => {
    setSummaries((prev) => {
      const copy = [...prev];
      const existing = copy[index] || {};
      const updated = { ...existing };
      if (field === "selectedOrganism") {
        updated.organismId = value;
      } else if (field === "colonyCount") {
        updated.colonyId = value;
      } else if (field === "internalComments") {
        updated.internalComments = value;
      } else if (field === "significantFindings") {
        updated.significantFinding = value;
      } else if (field === "bap" || field === "mac") {
        updated[field] = value;
      } else {
        updated[field] = value;
      }
      copy[index] = updated;
      return copy;
    });
  };

  const executePublishAndValidateApi = async () => {
    if (!requisitionId) {
      toast.error(t("Requisition ID is required"));
      return;
    }
    const res: AxiosResponse = await PublishAndValidate({
      requisitionId: requisitionId,
      requisitionOrderId: requisitionOrderId,
    });
    if (res?.data?.statusCode === 200) {
      toast.success(t(res?.data?.message || (filterData?.tabId === 5 ? "Re-published and validated" : "Published and validated")));
      loadGridData(false);
    } else {
      toast.error(t(res?.data?.message || (filterData?.tabId === 5 ? "Failed to re-publish and validate" : "Failed to publish and validate")));
    }
  };

  const handlePublishAndValidate = async () => {
    if (!requisitionId) {
      toast.error(t("Requisition ID is required"));
      return;
    }
    if (!sensitivityData || sensitivityData.length === 0) {
      toast.error(t("Please add sensitivity data or use No Sensitizer Required to publish."));
      return;
    }
    setIsUpdatingLisStatus(true);
    setUpdatingLisStatusAction("publish");
    try {
      await executePublishAndValidateApi();
    } catch (err: any) {
      toast.error(t(err?.response?.data?.message || (filterData?.tabId === 5 ? "Error re-publishing and validating" : "Error publishing and validating")));
    } finally {
      setIsUpdatingLisStatus(false);
      setUpdatingLisStatusAction(null);
    }
  };

  const handleNoSensitizerRequired = async () => {
    setIsNoSensitizerSaving(true);
    try {
      await executePublishAndValidateApi();
    } catch (err: any) {
      toast.error(t(err?.response?.data?.message || (filterData?.tabId === 5 ? "Error re-publishing and validating" : "Error publishing and validating")));
    } finally {
      setIsNoSensitizerSaving(false);
    }
  };

  return (
    <div
      ref={
        rowsToExpand.length > 0 &&
          requisitionOrderId === rowsToExpand[rowsToExpand.length - 2]
          ? targetRef
          : null
      }
      className="d-flex flex-column flex-column-fluid table-expend-sticky table-expend-sm-sticky"
    >
      <div id="kt_app_content" className="app-content flex-column-fluid pb-0">
        {loading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
            <Loader />
          </div>
        ) : (
          <div
            id="kt_app_content_container"
            className="app-container container-fluid mb-container"
          >
            <h6 className="text-primary h5">{t("Results Summary")}</h6>
            {summaries.length > 0 ? (
              summaries.map((s: any, idx: number) => (
                <div key={s.id || idx} className="card rounded mb-4" style={{ border: "2px solid orange" }}>
                  <div className="card-header px-4 d-flex justify-content-between align-items-center rounded bg-light-warning min-h-40px">
                    <h6 className="h5 mb-0">{t("Plate Details")}</h6>
                  </div>
                  <div className="card-body py-md-4 py-2 px-4">
                    <div className="col-xl-12 col-lg-12 col-md-12 col-sm-12">
                      <div className="row g-3">
                        <div className="row g-3 flex-wrap justify-content-between align-items-center">
                          <div className="col-xl-6 col-md-6 col-sm-12">
                            <div className="w-100">
                              <div>
                                <span>{t("Select Organism")}</span>
                                <Select
                                  inputId={`OrganismSelect_${requisitionOrderId}_${idx}`}
                                  menuPortalTarget={document.body}
                                  styles={reactSelectSMStyle2()}
                                  theme={(theme: any) => styles(theme)}
                                  placeholder={t("-- Select --")}
                                  name="selectedOrganism"
                                  isSearchable={true}
                                  isClearable={true}
                                  options={organismOptions}
                                  value={organismOptions.find(
                                    (option) => option.value == (s.organismId || 0)
                                  )}
                                  onChange={(selectedOption: any) => {
                                    updateSummaryField(idx, "selectedOrganism", selectedOption?.value || 0);
                                  }}
                                />
                              </div>
                            </div>
                          </div>

                          <div className="col-xl-6 col-md-6 col-sm-12">
                            <div className="w-100">
                              <div>
                                <span>{t("Colony Count")}</span>
                                <Select
                                  inputId={`ColonyCountSelect_${requisitionOrderId}_${idx}`}
                                  menuPortalTarget={document.body}
                                  styles={reactSelectSMStyle2()}
                                  theme={(theme: any) => styles(theme)}
                                  placeholder={t("-- Select --")}
                                  name="colonyCount"
                                  isSearchable={true}
                                  isClearable={true}
                                  options={colonyCountOptions}
                                  value={colonyCountOptions.find(
                                    (option) => option.value == (s.colonyId || 0)
                                  )}
                                  onChange={(selectedOption: any) => {
                                    updateSummaryField(idx, "colonyCount", selectedOption?.value || 0);
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="px-4">
                    <div className="row g-3 flex-wrap justify-content-between align-items-center">
                      <h6 className="text-primary h5">{t("Plate Description")}</h6>
                      {(s.plateDescriptions && s.plateDescriptions.length > 0) ? (
                        s.plateDescriptions.map((d: any, di: number) => (
                          <div className="col-xl-6 col-md-12 col-sm-12" key={d?.id || di}>
                            <div className="w-100">
                              <span>{d?.label}</span>
                              <input
                                id={`PlateDescInput_${requisitionOrderId}_${idx}_${di}`}
                                type="text"
                                className="form-control bg-white mb-3 mb-lg-0 rounded-2 h-30px fs-7 w-100"
                                placeholder={t("-- Type --")}
                                value={d?.description || ""}
                                onChange={(e: any) => {
                                  const text = e.target.value;

                                  setSummaries((prev: any) => {
                                    const copy = [...prev];
                                    const existing = copy[idx] || {};
                                    const descs = Array.isArray(existing.plateDescriptions)
                                      ? [...existing.plateDescriptions]
                                      : [];

                                    // match by descLISId (stable slot); id can be 0 for new organisms so don't match by id only
                                    const matchIndex = descs.findIndex(
                                      (pd: any) => pd.descLISId === d?.descLISId
                                    );

                                    const newEntry = {
                                      descLISId: d?.descLISId ?? 0,
                                      id: d?.id ?? null,
                                      label: d?.label ?? "",
                                      description: text,
                                    };

                                    if (matchIndex >= 0) {
                                      descs[matchIndex] = { ...descs[matchIndex], ...newEntry };
                                    } else {
                                      descs.push(newEntry);
                                    }

                                    copy[idx] = { ...existing, plateDescriptions: descs };
                                    return copy;
                                  });
                                }}
                              />
                            </div>
                          </div>
                        ))
                      ) : null}
                    </div>
                    <div className="row g-3 flex-wrap justify-content-between align-items-center mt-2">
                      <div className="col-xl-6 col-md-12 col-sm-12">
                        <span>{t("Internal Comments:")}</span>
                        <textarea
                          id={`CategorySetupAllTestListSearch_InternalComments_${requisitionOrderId}_${idx}`}
                          className="form-control bg-white mb-3 mb-lg-0 rounded-2 h-70px fs-7 w-100"
                          rows={5}
                          value={s.internalComments || ""}
                          onChange={(e) => updateSummaryField(idx, "internalComments", e.target.value)}
                        />
                      </div>
                      <div className="col-xl-6 col-md-12 col-sm-12">
                        <span>{t("Significant Findings:")}</span>
                        <textarea
                          id={`CategorySetupAllTestListSearch_SignificantFindings_${requisitionOrderId}_${idx}`}
                          className="form-control bg-white mb-3 mb-lg-0 rounded-2 h-70px fs-7 w-100"
                          rows={5}
                          value={s.significantFinding || ""}
                          onChange={(e) => updateSummaryField(idx, "significantFindings", e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="d-flex flex-wrap gap-2 my-4 px-4">
                    {Array.isArray(s.panels) && s.panels.length > 0 && s.panels.map((p: any, panelIdx: number) => (
                      <button
                        key={p.id || p.panelName}
                        type="button"
                        className="btn btn-sm fw-bold text-white"
                        style={{ background: PANEL_BUTTON_COLORS[panelIdx % PANEL_BUTTON_COLORS.length] }}
                        onClick={async () => {
                          try {
                            const response = await GetSensitiResultData({
                              reqOrderId: requisitionOrderId,
                              panelId: p.id ?? 0,
                            });
                            if (response?.data?.statusCode === 200 && response?.data?.data) {
                              const data = Array.isArray(response.data.data)
                                ? response.data.data
                                : [];
                              setSensitivityData(data);
                            } else {
                              setSensitivityData([]);
                            }
                          } catch (error) {
                            console.error("Error fetching sensitivity data:", error);
                            setSensitivityData([]);
                          }
                        }}
                      >
                        {p.panelName}
                      </button>
                    ))}
                    <PermissionComponent
                      moduleName="Microbiology LIS"
                      pageName="Result Data"
                      permissionIdentifier="NoSensitizerRequired"
                    >
                      {({ pageName, moduleName, permissionIdentifier }) => (
                        <button
                          type="button"
                          className="btn btn-linkedin btn-sm fw-500"
                          disabled={isNoSensitizerSaving}
                          onClick={handleNoSensitizerRequired}
                        >
                          {t(getPermissionDisplayName(pageName, moduleName, permissionIdentifier, "No Sensitizer Required"))}
                        </button>
                      )}
                    </PermissionComponent>
                  </div>
                </div>
              ))
            ) : null}

            {/* Sensitivity Data Table */}
            {sensitivityData.length > 0 && (
              <SensitivityDataTable
                data={sensitivityData}
                onMicChange={(id, mic) => {
                  // Handle MIC change if needed
                  console.log("MIC changed:", id, mic);
                }}
                onInterpretationChange={(id, interpretation) => {
                  // Handle interpretation change if needed
                  console.log("Interpretation changed:", id, interpretation);
                }}
              />
            )}

            <div className="d-flex flex-wrap gap-2 mt-4 mb-4">
              <PermissionComponent
                moduleName="Microbiology LIS"
                pageName="Result Data"
                permissionIdentifier="AddOrganism"
              >
                {({ pageName, moduleName, permissionIdentifier }) => (
                  <button
                    className="btn btn-sm fw-bold text-white"
                    style={{ background: "#28a745" }}
                    onClick={() => {
                  // Template plateDescriptions from first existing summary (same structure as GetResultSummary), id=0 and description cleared
                  setSummaries((prev) => {
                    const templatePlateDescriptions =
                      prev.length > 0 && Array.isArray(prev[0].plateDescriptions)
                        ? prev[0].plateDescriptions.map((pd: any) => ({
                          ...pd,
                          id: 0,
                          description: "",
                        }))
                        : [];
                    const templatePanels =
                      prev.length > 0 && Array.isArray(prev[0].panels)
                        ? prev[0].panels.map((p: any) => ({ ...p }))
                        : [];
                    const newObj = {
                      id: 0,
                      requsitionOrderId: requisitionOrderId,
                      organismId: 0,
                      organismName: "",
                      colonyId: 0,
                      colonyCount: "",
                      internalComments: "",
                      significantFinding: "",
                      tabId: filterData?.tabId || 0,
                      plateDescriptions: templatePlateDescriptions,
                      panels: templatePanels,
                    };
                    const next = [...prev, newObj];
                    // setActiveSummaryIndex(next.length - 1);
                    setPlateData({
                      selectedOrganism: "",
                      colonyCount: "",
                      bap: "",
                      mac: "",
                      internalComments: "",
                      significantFindings: "",
                      plateDescriptions: templatePlateDescriptions,
                    });
                    setPanelsFromSummary(templatePanels);
                    return next;
                  });
                }}
              >
                {t(getPermissionDisplayName(pageName, moduleName, permissionIdentifier, "Add Organism"))}
              </button>
                )}
              </PermissionComponent>
              {(filterData?.tabId === 2 || filterData?.tabId === 3 || filterData?.tabId === 4 || filterData?.tabId === 5) && (
                <PermissionComponent
                  moduleName="Microbiology LIS"
                  pageName="Result Data"
                  permissionIdentifier="ShowHistoricalData"
                >
                  {({ pageName, moduleName, permissionIdentifier }) => (
                    <button
                      id={`MicrobiologyResultDataExpandShowHistoricalData`}
                      className="btn btn-sm fw-bold text-white"
                      style={{ background: "#6ab8e2" }}
                      onClick={() => setShowViewHistoricalData(true)}
                    >
                      {t(getPermissionDisplayName(pageName, moduleName, permissionIdentifier, "Show Historical Data"))}
                    </button>
                  )}
                </PermissionComponent>
              )}
              {(filterData?.tabId === 2 || filterData?.tabId === 3 || filterData?.tabId === 4 || filterData?.tabId === 5) && (
                <PermissionComponent
                  moduleName="Microbiology LIS"
                  pageName="Result Data"
                  permissionIdentifier="Preview"
                >
                  {({ pageName, moduleName, permissionIdentifier }) => (
                    <button
                      type="button"
                      className="btn btn-sm fw-bold text-white"
                      style={{ background: "#ff9800" }}
                      onClick={async () => {
                        toast.error(t("Preview is not available"));
                        return;
                      }}
                    >
                      {t(getPermissionDisplayName(pageName, moduleName, permissionIdentifier, "Preview"))}
                    </button>
                  )}
                </PermissionComponent>
              )}
              <PermissionComponent
                moduleName="Microbiology LIS"
                pageName="Result Data"
                permissionIdentifier="Publish&Validate"
              >
                {({ pageName, moduleName, permissionIdentifier }) => (
                  <button
                    type="button"
                    className="btn btn-sm fw-bold text-white"
                    style={{ background: "#dc3545" }}
                    disabled={isUpdatingLisStatus}
                    onClick={handlePublishAndValidate}
                  >
                    {isUpdatingLisStatus && updatingLisStatusAction === "publish"
                      ? t("Publishing...")
                      : filterData?.tabId === 5
                        ? t(getPermissionDisplayName(pageName, moduleName, permissionIdentifier, "Re-Publish & Validate"))
                        : t(getPermissionDisplayName(pageName, moduleName, permissionIdentifier, "Publish & Validate"))}
                  </button>
                )}
              </PermissionComponent>
              <PermissionComponent
                moduleName="Microbiology LIS"
                pageName="Result Data"
                permissionIdentifier="Save"
              >
                {({ pageName, moduleName, permissionIdentifier }) => (
                  <button
                    id={`MicrobiologyResultDataExpandSave`}
                    className="btn btn-sm fw-bold text-white"
                    style={{ background: "#28a745" }}
                    disabled={isSubmitting}
                    onClick={() => saveMicrobiologyExpandResultData()}
                  >
                    {isSubmitting ? t("Saving") : t(getPermissionDisplayName(pageName, moduleName, permissionIdentifier, "Save"))}
                  </button>
                )}
              </PermissionComponent>
              {filterData?.tabId !== 5 && (
                <PermissionComponent
                  moduleName="Microbiology LIS"
                  pageName="Result Data"
                  permissionIdentifier="Complete"
                >
                  {({ pageName, moduleName, permissionIdentifier }) => (
                    <button
                      type="button"
                      className="btn btn-sm fw-bold text-white"
                      style={{ background: "#2196f3" }}
                      disabled={isUpdatingLisStatus}
                      onClick={async () => {
                    const tabId = filterData?.tabId ?? 0;
                    const completeLabel = tabId === 3 ? "Complete 48h" : tabId === 4 ? "Complete 72h" : "Complete 24h";
                    setIsUpdatingLisStatus(true);
                    setUpdatingLisStatusAction("complete");
                    try {
                      const res: AxiosResponse = await UpdateLisStatus({
                        reqOrderId: requisitionOrderId,
                        tabId,
                      });
                      if (res?.data?.statusCode === 200) {
                        toast.success(t(res?.data?.message || completeLabel));
                        loadGridData(false);
                      } else {
                        toast.error(t(res?.data?.message || `Failed to ${completeLabel.toLowerCase()}`));
                      }
                    } catch (err: any) {
                      toast.error(t(`Error ${completeLabel.toLowerCase()}`));
                    } finally {
                      setIsUpdatingLisStatus(false);
                      setUpdatingLisStatusAction(null);
                    }
                  }}
                >
                  {isUpdatingLisStatus && updatingLisStatusAction === "complete"
                    ? t("Completing...")
                    : t(getPermissionDisplayName(pageName, moduleName, permissionIdentifier, filterData?.tabId === 3 ? "Complete 48h" : filterData?.tabId === 4 ? "Complete 72h" : "Complete 24h"))}
                </button>
                  )}
                </PermissionComponent>
              )}
              {filterData?.tabId === 5 && (
                <PermissionComponent
                  moduleName="Microbiology LIS"
                  pageName="Result Data"
                  permissionIdentifier="Reject"
                >
                  {({ pageName, moduleName, permissionIdentifier }) => (
                    <button
                      type="button"
                      className="btn btn-sm fw-bold text-white"
                      style={{ background: "#e90e24" }}
                      onClick={() => setShowRejectModal(true)}
                    >
                      {t(getPermissionDisplayName(pageName, moduleName, permissionIdentifier, "Reject"))}
                    </button>
                  )}
                </PermissionComponent>
              )}
            </div>
          </div>
        )}
      </div>

      <ViewHistoricalDataModal
        show={showViewHistoricalData}
        onHide={() => setShowViewHistoricalData(false)}
        reqOrderId={requisitionOrderId}
        tabId={filterData?.tabId}
      />

      <RejectModal
        show={showRejectModal}
        onHide={() => setShowRejectModal(false)}
        requisitionOrderId={requisitionOrderId}
        requisitionId={requisitionId}
        onSuccess={() => {
          loadGridData(false);
        }}
      />
    </div>
  );
};

export default ResultDataExpandableRow;
